import { Directive, Inject, Injector, Optional, Self } from '@angular/core';

import { DISPLAY_TYPE, IWidgetConfig, StylableComponent, provideAsWidgetRef, Parent, styler, refreshDataSource } from "@wm/components-base";
import { TableComponent } from '../table.component';
import { $appDigest, App, DataSource, triggerFn, extractCurrentItemExpr, $invokeWatchers } from '@wm/core';
import { last, map, startsWith } from 'lodash-es';

const DEFAULT_CLS = '';
const WIDGET_CONFIG: IWidgetConfig = {
  widgetType: 'wm-table-cud',
  hostClass: DEFAULT_CLS
};

const OPERATION = {
  'NEW': 'new',
  'EDIT': 'edit',
  'DELETE': 'delete'
};

@Directive({
  selector: '[wmTableCud]',
  standalone: true,
  exportAs: 'wmTableCud'
})
export class TableCudDirective extends StylableComponent {
  private app: App

  constructor(inj: Injector,
    @Optional() viewParent: Parent,
    @Self() @Inject(TableComponent) private table: TableComponent) {
    super(inj, WIDGET_CONFIG, viewParent);
    styler(this.nativeElement, this);
    table.initiateSelectItem = this.initiateSelectItem.bind(this);
    table.updateVariable = this.updateVariable.bind(this);
    table.updateRecord = this.updateRecord.bind(this);
    // table.deleteRecord = this.deleteRecord.bind(this);
    table.insertRecord = this.insertRecord.bind(this);
    // table.editRow = this.editRow.bind(this);
    table.addNewRow = this.addNewRow.bind(this);
    table.addRow = this.addNewRow.bind(this);
    // table.deleteRow = this.deleteRow.bind(this);
    // table.onRecordDelete = this.onRecordDelete.bind(this);
    // table.hideEditRow = this.hideEditRow.bind(this);
    // table.saveRow = this.saveRow.bind(this);
    // table.cancelRow = this.cancelRow.bind(this);
  }

  addNewRow() {
    if (!this.table.isGridEditMode) { // If grid is already in edit mode, do not add new row
      this.table._triggeredByUser = true;
      this.table.callDataGridMethod('addNewRow');
      if (this.table._liveTableParent) {
        this.table._liveTableParent.addNewRow();
      } else if (this.table._isDependent) {
        this.triggerWMEvent('insert');
        //this.table.callDataGridMethod('addNewRow');
      }
    }
  }

  private triggerWMEvent(eventName: any, dataSource?: any) {
    $invokeWatchers(true);
    this.app.notify('wm-event', { eventName, widgetName: this.table.name, row: this.table.selecteditem, dataSource: dataSource });
  }

  insertRecord(options: any) {
    const dataSource = this.table.datasource;
    if (!dataSource) {
      return;
    }
    const currentPageNum = dataSource.pagination && dataSource.pagination.number + 1;
    const dataObject = {
      row: options.row,
      skipNotification: true,
      period: options.period
    };
    if (dataSource.execute(DataSource.Operation.SUPPORTS_CRUD) || !dataSource.execute(DataSource.Operation.IS_API_AWARE) || dataSource.category === 'wm.CrudVariable') {
      if (!dataSource.execute(DataSource.Operation.IS_API_AWARE)) {
        const extraOptions = this.generatePath(this.table.binddataset);
        dataSource.execute(DataSource.Operation.ADD_ITEM, { item: options.row, path: extraOptions.path, parentIndex: extraOptions.index });
        this.insertSuccessHandler(options.row, options);
        return;
      }
      dataSource.execute(DataSource.Operation.INSERT_RECORD, dataObject).then((response: any) => {
        this.insertSuccessHandler(response, options);
        if (dataSource.category === 'wm.CrudVariable') {
          dataSource.execute(DataSource.Operation.LIST_RECORDS, {
            'skipToggleState': true,
            'operation': 'list',
            'page': currentPageNum
          });
        }
      }, (error: any) => {
        this.table.invokeEventCallback('error', { $event: options.event, $operation: OPERATION.NEW, $data: error });
        if (!this.table.onError) {
          this.table.toggleMessage(true, 'error', this.table.errormessage || error);
        }
        triggerFn(options.error, error);
        triggerFn(options.callBack, undefined, true);
      });
    } else {
      this.table.invokeEventCallback('rowinsert', { $event: options.event, row: options.row });
    }
  }

  private insertSuccessHandler(response: any, options: any) {
    /*Display appropriate error message in case of error.*/
    if (response.error) {
      this.table.invokeEventCallback('error', { $event: options.event, $operation: OPERATION.NEW, $data: response.error });
      if (!this.table.onError) {
        this.table.toggleMessage(true, 'error', this.table.errormessage || response.error);
      }
      triggerFn(options.error, response);
    } else {
      if (this.table.gridOptions.isNavTypeScrollOrOndemand()) {
        this.table.isNewRowInserted = true;
      }
      if (options.event) {
        const row = $(options.event.target).closest('tr');
        this.table.callDataGridMethod('hideRowEditMode', row);
      }
      if (!this.table.onRowinsert) {
        this.table.toggleMessage(true, 'success', this.table.insertmessage);
      }
      if (this.table.datasource.execute(DataSource.Operation.SUPPORTS_CRUD)) {
        this.table.initiateSelectItem(this.table.getNavigationTargetBySortInfo(), response, undefined, false, options.callBack);
        this.updateVariable(response, options.callBack);
      } else if (!this.table.datasource.execute(DataSource.Operation.IS_API_AWARE)) {
        this.table.initiateSelectItem(this.table.getNavigationTargetBySortInfo(), response, undefined, false, options.callBack);
      }
      triggerFn(options.success, response);
      this.table.invokeEventCallback('rowinsert', { $event: options.event, $data: response, row: response });
    }
  }

  updateVariable(row?: any, callBack?: any) {
    const dataSource = this.table.datasource;
    if (!dataSource) {
      return;
    }

    dataSource.execute(DataSource.Operation.FETCH_DISTINCT_VALUES);

    if (!this.table.isNavigationEnabled()) {
      const sortInfo = this.table.sortInfo;
      const sortOptions = sortInfo && sortInfo.direction ? (sortInfo.field + ' ' + sortInfo.direction) : '';
      refreshDataSource(dataSource, {
        page: 1,
        filterFields: this.table.getFilterFields(this.table.filterInfo),
        orderBy: sortOptions,
        matchMode: 'anywhereignorecase'
      }).then(() => {
        $appDigest();
        this.selectItemOnSuccess(row, true, callBack);
      });
    }
  }

  updateRecord(options: any) {
    const dataSource = this.table.datasource;
    if (!dataSource) {
      return;
    }
    const currentPageNum = dataSource.pagination && dataSource.pagination.number + 1;
    const dataObject = {
      row: options.row,
      prevData: options.prevData,
      skipNotification: true,
      period: options.period
    };

    if (dataSource.execute(DataSource.Operation.SUPPORTS_CRUD) || !dataSource.execute(DataSource.Operation.IS_API_AWARE) || dataSource.category === 'wm.CrudVariable') {
      if (!dataSource.execute(DataSource.Operation.IS_API_AWARE)) {
        const extraOptions = this.generatePath(this.table.binddataset);
        dataSource.execute(DataSource.Operation.SET_ITEM, { prevItem: options.prevData, item: options.row, path: extraOptions.path, parentIndex: extraOptions.index });
        this.updateSuccessHandler(options.row, options);
        return;
      }
      dataSource.execute(DataSource.Operation.UPDATE_RECORD, dataObject).then((response: any) => {
        this.updateSuccessHandler(response, options);
        if (dataSource.category === 'wm.CrudVariable') {
          dataSource.execute(DataSource.Operation.LIST_RECORDS, {
            'skipToggleState': true,
            'operation': 'list',
            'page': currentPageNum
          });
        }
      }, (error: any) => {
        this.table.invokeEventCallback('error', { $event: options.event, $operation: OPERATION.EDIT, $data: error });
        if (!this.table.onError) {
          this.table.toggleMessage(true, 'error', this.table.errormessage || error);
        }
        triggerFn(options.error, error);
        triggerFn(options.callBack, undefined, true);
      });
    } else {
      this.table.invokeEventCallback('rowupdate', { $event: options.event, row: options.row });
    }
  }

  private updateSuccessHandler(response: any, options: any) {
    /*Display appropriate error message in case of error.*/
    if (response.error) {
      this.table.invokeEventCallback('error', { $event: options.event, $operation: OPERATION.EDIT, $data: response.error });
      /*disable readonly and show the appropriate error*/
      if (!this.table.onError) {
        this.table.toggleMessage(true, 'error', this.table.errormessage || response.error);
      }
      triggerFn(options.error, response);
    } else {
      if (options.event) {
        const row = $(options.event.target).closest('tr');
        this.table.callDataGridMethod('hideRowEditMode', row);
      }
      if (!this.table.onRowupdate) {
        this.table.toggleMessage(true, 'success', this.table.updatemessage);
      }
      if (this.table.datasource.execute(DataSource.Operation.SUPPORTS_CRUD)) {
        this.table.initiateSelectItem('current', response, undefined, false, options.callBack);
        this.updateVariable(response, options.callBack);
      } else if (!this.table.datasource.execute(DataSource.Operation.IS_API_AWARE)) {
        this.table.initiateSelectItem('current', response, undefined, false, options.callBack);
      }
      triggerFn(options.success, response);
      this.table.invokeEventCallback('rowupdate', { $event: options.event, $data: response, row: response });
    }
  }

  generatePath(binddataset: any) {
    let path, index;
    let dataBoundExpr = this.table.widget.$attrs.get('datasetboundexpr');
    if (startsWith(binddataset, 'item') && dataBoundExpr) {
      if (startsWith(dataBoundExpr, 'Widgets.')) {
        dataBoundExpr = extractCurrentItemExpr(dataBoundExpr, this.table);
      }
      const parentListItems = this.table.$element.parents('.app-list-item');
      const indexArr = map(parentListItems, (item) => parseInt(($(item) as any).attr('listitemindex')));
      for (let i = indexArr.length - 1; i >= 0; i--) {
        dataBoundExpr = dataBoundExpr.replace('$i', indexArr[i]);
      }
      index = last(indexArr);
      path = dataBoundExpr.replace(/^Variables\..*\.dataSet([^.])*(\.|$)/g, '');
    } else {
      // if we have dataset as "Variables.staticVar1.dataSet[1].details" then pass index as 1.
      const regEx = /^Variables\..*\.dataSet([\[][0-9]])/g;
      if (regEx.test(binddataset)) {
        index = parseInt(binddataset.replace(/^Variables\..*\.dataSet([\[])/g, ''));
      }
      path = binddataset.replace(/^Variables\..*\.dataSet([^.])*(\.|$)/g, '');
    }
    return { path, index };
  }

  initiateSelectItem(index: any, row: any, skipSelectItem?: any, isStaticVariable?: any, callBack?: any) {
    // In case of on demand and Infinite Scroll pagination, when new row is inserted show it in the last page and continue with current flow instead of navigating user to the last page
    if ((this.table.gridOptions.isNavTypeScrollOrOndemand()) && !this.table.dataNavigator.isDisableNext && this.table.isNewRowInserted) {
      this.table.isNewRowInserted = false;
      return;
    }
    /*index === "last" indicates that an insert operation has been successfully performed and navigation to the last page is required.
     * Hence increment the "dataSize" by 1.*/
    if (index === 'last') {
      if (!isStaticVariable) {
        this.table.dataNavigator.dataSize += 1;
      }
      /*Update the data in the current page in the grid after insert/update operations.*/
      if (!this.table.isNavigationEnabled()) {
        index = 'current';
      }
    }
    /*Re-calculate the paging values like pageCount etc that could change due to change in the dataSize.*/
    this.table.dataNavigator.calculatePagingValues();
    /* updating pagination fulldata*/
    this.table.dataNavigator.__fullData = this.table.__fullData;
    this.table.dataNavigator.navigatePage(index, null, true, () => {
      if (this.table.isNavigationEnabled() || isStaticVariable) {
        this.selectItemOnSuccess(row, skipSelectItem, callBack);
      }
      if (this.table.editmode === 'quickedit' && !skipSelectItem) {
        // In case of quickeditmode, manually set active row to attach event handlers and focus the row
        this.table.callDataGridMethod('setActiveRow', row);
      }
    });
  }

  private selectItemOnSuccess(row: any, skipSelectItem: any, callBack: any) {
    /*$timeout is used so that by then $is.dataset has the updated value.
     * Selection of the item is done in the callback of page navigation so that the item that needs to be selected actually exists in the grid.*/
    /*Do not select the item if skip selection item is specified*/
    setTimeout(() => {
      if (!skipSelectItem) {
        this.table.selectItem(row, this.table.dataset);
      }
      triggerFn(callBack);
    }, 250);
  }
}

